import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiGlobe, FiChevronDown, FiChevronUp, FiCheck, FiSearch } from 'react-icons/fi';
import { useI18n, SUPPORTED_LANGUAGES } from './utils/i18n';
import './LanguageSwitch.css';

// Number of languages beyond which the dropdown grows an inline search box.
// Below this the list is short enough to scan at a glance.
const SEARCH_THRESHOLD = 8;

// Shared "menu bus": any header popover dispatches this when it opens so the
// others close themselves, keeping at most one open at a time.
const MENU_OPEN_EVENT = 'jolymei:menu-open';
const MENU_ID = 'language';

// A trigger whose open style changes the border colour spells its border out
// in longhands: with the `border` shorthand, React clears the colour when the
// open style goes away and the border falls back to the text colour.
const styles = {
  glass: {
    wrapper: { position: 'relative', display: 'inline-block' },
    trigger: {
      boxSizing: 'border-box',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      minWidth: '8rem',
      borderRadius: '0.9rem',
      padding: '0.62rem 0.85rem',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'rgba(148,163,184,0.22)',
      background: 'rgba(2,6,23,0.62)',
      color: '#cbd5e1',
      fontWeight: 800,
      fontSize: '0.9rem',
      cursor: 'pointer',
      boxShadow: '0 16px 42px rgba(0,0,0,0.24)',
      backdropFilter: 'blur(14px)',
      transition: 'border-color 0.2s ease, color 0.2s ease',
    },
    triggerOpen: { borderColor: 'rgba(34,211,238,0.45)', color: '#e2e8f0' },
    menu: {
      borderRadius: '0.9rem',
      padding: '0.35rem',
      border: '1px solid rgba(148,163,184,0.22)',
      background: 'rgba(2,6,23,0.92)',
      boxShadow: '0 16px 42px rgba(0,0,0,0.5)',
      backdropFilter: 'blur(14px)',
    },
    option: { borderRadius: '0.6rem', color: '#cbd5e1' },
    activeOption: { background: 'rgba(34,211,238,0.18)', color: '#a5f3fc' },
    accent: '#a5f3fc',
  },
  // Text-only trigger for the homepage nav, where only the sign-up action keeps
  // a border; the menu stays opaque instead of blurring the page behind it.
  plain: {
    wrapper: { position: 'relative', display: 'inline-block' },
    trigger: {
      boxSizing: 'border-box',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.45rem',
      borderRadius: '0.6rem',
      padding: '0.62rem 0.6rem',
      border: '1px solid transparent',
      background: 'transparent',
      color: '#cbd5e1',
      fontWeight: 600,
      fontSize: '0.9rem',
      cursor: 'pointer',
      transition: 'color 0.2s ease',
    },
    triggerOpen: { color: '#f8fafc' },
    menu: {
      borderRadius: '0.9rem',
      padding: '0.35rem',
      border: '1px solid rgba(148,163,184,0.22)',
      background: 'rgba(2,6,23,0.96)',
      boxShadow: '0 16px 42px rgba(0,0,0,0.5)',
    },
    option: { borderRadius: '0.6rem', color: '#cbd5e1' },
    activeOption: { background: 'rgba(143,211,255,0.14)', color: '#e0f2fe' },
    accent: '#bae6fd',
  },
  // Full-width row in the module drawer's account card. The card sits at the
  // bottom of the drawer, so the list opens upward, and the trigger shows the
  // language's full name since there is room for it.
  drawer: {
    wrapper: { position: 'relative', display: 'block', width: '100%' },
    trigger: {
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      width: '100%',
      borderRadius: 'var(--r-md)',
      padding: '0.5rem 0.7rem',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'rgb(30,41,59)',
      background: 'rgba(15,23,42,0.8)',
      color: '#e2e8f0',
      fontWeight: 600,
      fontSize: '0.8125rem',
      cursor: 'pointer',
      transition: 'border-color 0.2s ease, color 0.2s ease',
    },
    triggerOpen: { borderColor: 'rgba(125,211,252,0.45)', color: '#f8fafc' },
    menu: {
      borderRadius: 'var(--r-md)',
      padding: '0.35rem',
      border: '1px solid rgb(30,41,59)',
      background: 'rgba(2,6,23,0.98)',
      boxShadow: '0 -12px 32px rgba(0,0,0,0.45)',
    },
    menuPosition: { top: 'auto', bottom: 'calc(100% + 0.5rem)', left: 0, right: 0, width: 'auto' },
    option: { borderRadius: '0.5rem', color: '#cbd5e1' },
    activeOption: { background: 'rgba(56,189,248,0.15)', color: '#e0f2fe' },
    accent: '#bae6fd',
    showNativeName: true,
    opensUp: true,
  },
};

const menuPositionStyle = {
  position: 'absolute',
  top: 'calc(100% + 0.5rem)',
  right: 0,
  zIndex: 50,
  width: '12.5rem',
  maxHeight: '15rem',
  overflowY: 'auto',
};

const optionBaseStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  width: '100%',
  border: 'none',
  background: 'transparent',
  textAlign: 'left',
  padding: '0.55rem 0.75rem',
  fontSize: '0.9rem',
  fontWeight: 600,
  cursor: 'pointer',
};

export default function LanguageSwitch({ variant = 'glass', className = '', buttonClassName = '', label = 'Language switch' }) {
  const { language: currentLanguage, setLanguage } = useI18n();
  const variantStyles = styles[variant] || styles.glass;
  const ClosedChevron = variantStyles.opensUp ? FiChevronUp : FiChevronDown;
  const OpenChevron = variantStyles.opensUp ? FiChevronDown : FiChevronUp;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(-1);

  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const searchRef = useRef(null);

  const showSearch = SUPPORTED_LANGUAGES.length > SEARCH_THRESHOLD;
  const current = useMemo(
    () => SUPPORTED_LANGUAGES.find((option) => option.value === currentLanguage),
    [currentLanguage]
  );

  const options = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return SUPPORTED_LANGUAGES;
    return SUPPORTED_LANGUAGES.filter((option) =>
      `${option.nativeName} ${option.label} ${option.value}`.toLowerCase().includes(term)
    );
  }, [query]);

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
    setHighlight(-1);
  }, []);

  const openMenu = useCallback(() => {
    window.dispatchEvent(new CustomEvent(MENU_OPEN_EVENT, { detail: MENU_ID }));
    setOpen(true);
  }, []);

  const choose = useCallback(
    // Only return focus to the trigger for keyboard selections — refocusing after
    // a mouse click would trigger :focus-visible and leave a ring on the trigger.
    (value, { returnFocus = false } = {}) => {
      setLanguage(value);
      close();
      if (returnFocus) triggerRef.current?.focus();
    },
    [setLanguage, close]
  );

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) close();
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open, close]);

  // Close when another header menu opens (mutual exclusion via the menu bus).
  useEffect(() => {
    const onOtherMenuOpen = (event) => {
      if (event.detail !== MENU_ID) close();
    };
    window.addEventListener(MENU_OPEN_EVENT, onOtherMenuOpen);
    return () => window.removeEventListener(MENU_OPEN_EVENT, onOtherMenuOpen);
  }, [close]);

  // When opening, focus the search box (if present) and highlight the active item.
  useEffect(() => {
    if (!open) return;
    const activeIndex = options.findIndex((option) => option.value === currentLanguage);
    setHighlight(activeIndex);
    if (showSearch) searchRef.current?.focus();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const moveHighlight = useCallback(
    (delta) => {
      setHighlight((prev) => {
        if (options.length === 0) return -1;
        const next = prev + delta;
        if (next < 0) return options.length - 1;
        if (next >= options.length) return 0;
        return next;
      });
    },
    [options.length]
  );

  const onTriggerKeyDown = (event) => {
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openMenu();
    }
  };

  const onMenuKeyDown = (event) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        moveHighlight(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        moveHighlight(-1);
        break;
      case 'Home':
        event.preventDefault();
        setHighlight(0);
        break;
      case 'End':
        event.preventDefault();
        setHighlight(options.length - 1);
        break;
      case 'Enter':
        event.preventDefault();
        if (options[highlight]) choose(options[highlight].value, { returnFocus: true });
        break;
      case 'Escape':
        // An open list takes Escape for itself, so the drawer or modal it sits
        // in stays open; with the list closed, Escape passes through to them.
        if (!open) break;
        event.preventDefault();
        event.stopPropagation();
        close();
        triggerRef.current?.focus();
        break;
      default:
        break;
    }
  };

  return (
    <div ref={containerRef} className={className} style={variantStyles.wrapper} onKeyDown={onMenuKeyDown}>
      <button
        ref={triggerRef}
        type="button"
        className={['language-switch__trigger', buttonClassName].filter(Boolean).join(' ')}
        onClick={() => (open ? close() : openMenu())}
        onKeyDown={onTriggerKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        style={{ ...variantStyles.trigger, ...(open ? variantStyles.triggerOpen : null) }}
      >
        <FiGlobe size={16} aria-hidden="true" style={{ flex: '0 0 auto', color: variantStyles.accent }} />
        <span lang={current?.htmlLang} style={{ flex: 1, textAlign: 'left' }}>
          {(variantStyles.showNativeName ? current?.nativeName : current?.label) ?? current?.nativeName ?? '—'}
        </span>
        {open ? (
          <OpenChevron size={15} aria-hidden="true" style={{ flex: '0 0 auto', color: variantStyles.accent }} />
        ) : (
          <ClosedChevron size={15} aria-hidden="true" style={{ flex: '0 0 auto', color: '#64748b' }} />
        )}
      </button>

      {open ? (
        <div role="listbox" aria-label={label} style={{ ...menuPositionStyle, ...variantStyles.menuPosition, ...variantStyles.menu }}>
          {showSearch ? (
            <div style={{ position: 'relative', marginBottom: '0.35rem' }}>
              <FiSearch
                size={14}
                aria-hidden="true"
                style={{ position: 'absolute', left: '0.6rem', top: '50%', transform: 'translateY(-50%)', color: '#64748b' }}
              />
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setHighlight(0);
                }}
                placeholder="Search…"
                aria-label="Search languages"
                style={{
                  boxSizing: 'border-box',
                  width: '100%',
                  padding: '0.45rem 0.6rem 0.45rem 1.7rem',
                  borderRadius: '0.5rem',
                  border: '1px solid rgba(148,163,184,0.2)',
                  background: 'rgba(15,23,42,0.6)',
                  color: '#e2e8f0',
                  fontSize: '0.85rem',
                  outline: 'none',
                }}
              />
            </div>
          ) : null}

          {options.length === 0 ? (
            <div style={{ padding: '0.55rem 0.75rem', color: '#64748b', fontSize: '0.85rem' }}>No matches</div>
          ) : (
            options.map((option, index) => {
              const active = option.value === currentLanguage;
              const highlighted = index === highlight;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => choose(option.value)}
                  onMouseEnter={() => setHighlight(index)}
                  style={{
                    ...optionBaseStyle,
                    ...variantStyles.option,
                    ...(highlighted && !active ? { background: 'rgba(148,163,184,0.12)' } : null),
                    ...(active ? variantStyles.activeOption : null),
                  }}
                >
                  <span lang={option.htmlLang} style={{ flex: 1 }}>{option.nativeName}</span>
                  {active ? (
                    <FiCheck size={15} aria-hidden="true" style={{ flex: '0 0 auto', color: variantStyles.accent }} />
                  ) : null}
                </button>
              );
            })
          )}
        </div>
      ) : null}
    </div>
  );
}
